/**
 * One line that says how the studio reads the base wall (rows or columns) and offers the
 * other reading in one click. Everything Finalize gates on the reading follows the choice.
 * Jira DEV-3451.
 */
import styled from "styled-components";
import { Button, Flex, InfoTooltip, Text } from "src/elements";
import { Color } from "src/elements/Color/Color";
import { useTranslation } from "src/i18n";
import { wallReadingOf } from "../domain/horizontalBlocking";
import type { StudioDraft, WallBlocking, WallBlockingKind } from "../domain/studioTypes";
import { STUDIO_THEME } from "../constants/studioTheme";

export type BrandShelves = NonNullable<StudioDraft["brandShelves"]>;

/** The default first: a release without the choice ships "whereClose". */
const BRAND_SHELVES: readonly BrandShelves[] = ["whereClose", "full", "sales"];

const BrandShelvesSelect = styled.select`
  padding: 1px 4px;
  border: 1px solid ${Color.spaceGray};
  border-radius: 4px;
  background: ${Color.white};
  color: ${Color.textMain};
  font: inherit;
  font-size: 12px;
`;

interface Props {
  wall: WallBlocking | undefined;
  isEditable: boolean;
  onChoose: (blocking: WallBlockingKind) => void;
  /** Horizontal walls only: how brands take shelves on store planograms. */
  brandShelves?: BrandShelves;
  onChooseBrandShelves?: (choice: BrandShelves) => void;
}

export const WallReadingLine = ({
  wall,
  isEditable,
  onChoose,
  brandShelves,
  onChooseBrandShelves,
}: Props) => {
  const { t } = useTranslation("cdtStudio");
  if (!wall) return null;
  const reading = wallReadingOf(wall);
  const rows = reading.blocking === "horizontal";
  const sentence = reading.overridden
    ? rows
      ? t("panel.wallReading.setRows")
      : t("panel.wallReading.setColumns")
    : rows
      ? t("panel.wallReading.readsRows")
      : t("panel.wallReading.readsColumns");
  const detail = t("panel.wallReading.detail", {
    attr: reading.branchAttr,
    score: reading.branchScore.toFixed(2),
    threshold: reading.verticalScore.toFixed(2),
    band: (reading.branchBand ?? 0).toFixed(2),
    bandThreshold: reading.rowScore.toFixed(2),
    share: Math.round(100 * reading.share),
    shareThreshold: Math.round(100 * reading.horizontalShare),
  });
  return (
    <Flex
      align="center"
      gap="6px"
      padding="0 12px 4px 12px"
      style={{ flexWrap: "wrap" }}
      data-testid="cdt-studio-wall-reading"
    >
      <Text variant="small1" color={STUDIO_THEME.text} testId="cdt-studio-wall-reading-text">
        {sentence}
      </Text>
      {rows && (
        <Text variant="small1" color={STUDIO_THEME.text} testId="cdt-studio-wall-reading-store">
          {t("panel.wallReading.storeUsesRows")}
        </Text>
      )}
      {rows && onChooseBrandShelves && (
        <Flex align="center" gap="4px" data-testid="cdt-studio-brand-shelves">
          <Text variant="small1" color={STUDIO_THEME.text}>
            {t("panel.wallReading.brandShelves.label")}
          </Text>
          <BrandShelvesSelect
            aria-label={t("panel.wallReading.brandShelves.label")}
            value={brandShelves ?? "whereClose"}
            disabled={!isEditable}
            onChange={(event) => onChooseBrandShelves(event.currentTarget.value as BrandShelves)}
          >
            {BRAND_SHELVES.map((choice) => (
              <option key={choice} value={choice}>
                {t(`panel.wallReading.brandShelves.${choice}`)}
              </option>
            ))}
          </BrandShelvesSelect>
          <InfoTooltip
            tooltip={t("panel.wallReading.brandShelves.help")}
            accessibleLabel={t("panel.wallReading.brandShelves.help")}
          />
        </Flex>
      )}
      {reading.closeCall && (
        <Text
          variant="small1"
          bold
          color={STUDIO_THEME.severity.toCheck.text}
          testId="cdt-studio-wall-reading-close-call"
        >
          {t("panel.wallReading.closeCall")}
        </Text>
      )}
      <InfoTooltip tooltip={detail} accessibleLabel={detail} />
      <Button
        size="small"
        variant="borderless"
        name="cdt-studio-wall-reading-flip"
        isDisabled={!isEditable}
        onClick={() => onChoose(rows ? "vertical" : "horizontal")}
      >
        {rows ? t("panel.wallReading.readAsColumns") : t("panel.wallReading.readAsRows")}
      </Button>
      <Text
        variant="small2"
        color={STUDIO_THEME.textSecondary}
        testId="cdt-studio-wall-reading-hint"
      >
        {t("panel.wallReading.flipHint")}
      </Text>
    </Flex>
  );
};
